// 본 모듈은 Edge 미들웨어 (`src/middleware.ts`) import 그래프에 들어가므로
// `@/Env` 처럼 PayPal/NaverPay/Notion config 가 함께 묶인 모듈은 의도적으로
// 끌어오지 않는다. 환경 판정은 `Env.ts:99` 와 동일한 canonical 패턴
// (`NEXT_PUBLIC_ENV === "production"`) 을 인라인으로 유지.
// NODE_ENV 사용 금지 — dev.bhaptics.com 도 production 으로 잡혀 scope drift
// 발생 (docs/pitfalls.md §5).
const isProd = process.env.NEXT_PUBLIC_ENV === "production";

// 쿠키 도메인은 NEXT_PUBLIC_ENV 가 아니라 "실제 host" 로 결정한다.
// 이유: `NEXT_PUBLIC_ENV=development` 가 서로 다른 두 host 에서 동시에 쓰임 —
//   - 로컬 `npm run dev`  → host = localhost        (host-only 필요)
//   - 배포된 dev 서버      → host = dev.bhaptics.com  (`.dev.bhaptics.com` 필요)
// env 로 도메인을 고정하면 둘 중 하나는 깨진다 (localhost 에서 `.dev.bhaptics.com`
// 쿠키는 브라우저가 host mismatch 로 거부 → 쿠키 silent fail). host 로 판단하면 양쪽 OK.
// 서브도메인 공유를 위해 leading dot 부착. localhost/IP 는 dot-domain 불가 → host-only.
// `resolveCookieDomain` 이 반환할 수 있는 dot-domain 스코프의 **단일 소스**.
// resolveCookieDomain(set 시 스코프 결정)과 deleteCookieAllScopes(cleanup 시 후보 스코프)가
// 이 목록을 공유해야, 새 서브도메인 추가 시 한 곳만 고치면 둘 다 반영된다(스코프 드리프트=중복
// 쿠키 버그 재발 방지). host-only(undefined)는 항상 별도 취급이라 여기 포함하지 않는다.
const KNOWN_COOKIE_DOMAINS = [".dev.bhaptics.com", ".bhaptics.com"] as const;

export function resolveCookieDomain(
  hostname: string | undefined,
): string | undefined {
  if (!hostname) return undefined;
  const h = hostname.toLowerCase();
  if (
    h === "localhost" ||
    h.endsWith(".localhost") ||
    h === "127.0.0.1" ||
    h === "[::1]"
  ) {
    return undefined;
  }
  // dev 서브도메인 먼저 검사 — dev.bhaptics.com 도 `.bhaptics.com` 으로 끝나므로 순서 중요
  // (KNOWN_COOKIE_DOMAINS 는 이 우선순위대로 나열: 더 구체적인 dev 서브도메인이 앞).
  for (const domain of KNOWN_COOKIE_DOMAINS) {
    // domain=".dev.bhaptics.com" → suffix ".dev.bhaptics.com" / bare "dev.bhaptics.com" 매칭.
    const bare = domain.slice(1);
    if (h === bare || h.endsWith(domain)) return domain;
  }
  return undefined; // 알 수 없는 host → host-only 안전 기본값
}

// 클라이언트 번들은 브라우저에서 평가되므로 실제 host 로 도메인이 잡힌다.
// 서버(미들웨어/SSR)는 window 가 없어 undefined 이며, 미들웨어는 호출처에서
// `resolveCookieDomain(req.nextUrl.hostname)` 로 per-request override 한다.
const clientCookieDomain =
  typeof window !== "undefined"
    ? resolveCookieDomain(window.location.hostname)
    : undefined;

// 서브도메인 간 공유가 필요한 쿠키 공통 옵션.
export const BHAPTICS_COOKIE_OPTS = {
  path: "/" as const,
  sameSite: "lax" as const,
  secure: isProd,
  domain: clientCookieDomain,
};

// deleteCookieAllScopes 가 순회할 후보 스코프 = host-only(undefined) + 알려진 dot-domain 전부.
// KNOWN_COOKIE_DOMAINS 에서 파생하므로 set 스코프(resolveCookieDomain)와 자동 정합 — 새 서브도메인
// 추가 시 KNOWN_COOKIE_DOMAINS 만 고치면 set·delete 양쪽 반영(드리프트 재발 방지). 모듈 1회 계산.
const ALL_COOKIE_SCOPES: ReadonlyArray<string | undefined> = [
  undefined,
  ...KNOWN_COOKIE_DOMAINS,
];

// 동일 이름 쿠키가 여러 도메인 스코프로 중복 등록되는 것을 제거한다.
// 문제: set 은 현재 host 스코프(`.dev.bhaptics.com` 등)로 하는데, 과거 다른 스코프
// (`.bhaptics.com` prod 방문 / host-only 구버전 / 서브도메인)로 쌓인 동일 이름 쿠키가
// 남으면 브라우저가 `document.cookie` 에 **둘 다** 노출 → Global-E JS 같은 소비자가
// 어느 쪽을 읽을지 비결정적(첫 번째 stale 값 → 엉뚱한 country/currency). (docs/pitfalls.md)
// deleteCookie 는 set 당시 스코프와 **정확히 일치**해야 지워지므로, 알려진 후보 스코프를 모두 시도.
// deleteFn 주입: cookie.ts 는 Edge 미들웨어 import 그래프라 cookies-next 를 직접 끌어오지 않기 위함.
export function deleteCookieAllScopes(
  name: string,
  deleteFn: (n: string, opts?: { domain?: string; path?: string }) => void,
): void {
  for (const domain of ALL_COOKIE_SCOPES) {
    deleteFn(name, domain ? { domain, path: "/" } : { path: "/" });
  }
}

const COUNTRY_CODE_PATTERN = /^[A-Z]{2}$/i;

// raw cookie / header 값을 ISO 3166-1 alpha-2 로 정규화. cookies-next 의
// string|boolean|undefined, req.headers 의 string|string[]|undefined 를 모두 안전하게 처리.
export function parseCountryCode(raw: unknown): string | undefined {
  return typeof raw === "string" && COUNTRY_CODE_PATTERN.test(raw)
    ? raw.toUpperCase()
    : undefined;
}
